import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import AdminLayout from "./AdminLayout";
import { adminListPosts, adminDeletePost } from "../../lib/api";
import { categoryLabel } from "../../lib/categories";
import { toast } from "sonner";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState(null);

  const load = async () => {
    try {
      const data = await adminListPosts();
      setPosts(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Failed to load posts.");
      setPosts([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await adminDeletePost(id);
      toast.success("Post deleted.");
      load();
    } catch {
      toast.error("Could not delete post.");
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-7">
        <div>
          <h1 className="font-heading font-light text-3xl tracking-tight text-[#0F172A]">
            Posts
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Drafts are private. Published posts appear instantly on{" "}
            <span className="font-mono">/blog</span>.
          </p>
        </div>
        <Link
          to="/admin/posts/new"
          data-testid="admin-new-post"
          className="btn-gold text-sm"
        >
          <Plus className="w-4 h-4" /> New post
        </Link>
      </div>

      {posts === null ? (
        <p className="text-slate-500 text-sm">Loading…</p>
      ) : posts.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white rounded-sm p-12 text-center">
          <p className="text-slate-500">No posts yet.</p>
          <Link
            to="/admin/posts/new"
            className="btn-gold inline-flex mt-5 text-sm"
          >
            Write your first post
          </Link>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-posts-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="py-3 px-4">Title</th>
                <th className="py-3 px-4 hidden md:table-cell">Category</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 hidden md:table-cell">Updated</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => {
                const updated = new Date(p.updated_at).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                });
                return (
                  <tr
                    key={p.id}
                    className="border-t border-slate-100 hover:bg-slate-50/60"
                  >
                    <td className="py-3 px-4">
                      <Link
                        to={`/admin/posts/${p.id}/edit`}
                        className="font-heading font-semibold text-[#0F172A] hover:text-[#3366FF]"
                      >
                        {p.title}
                      </Link>
                      <div className="text-xs text-slate-500 mt-0.5 font-mono">
                        /blog/{p.slug}
                      </div>
                    </td>
                    <td className="py-3 px-4 hidden md:table-cell text-slate-600">
                      {categoryLabel(p.category)}
                    </td>
                    <td className="py-3 px-4">
                      {p.status === "published" ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <Eye className="w-3 h-3" /> Published
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm bg-slate-100 text-slate-600 border border-slate-200">
                          <EyeOff className="w-3 h-3" /> Draft
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 hidden md:table-cell text-slate-500">
                      {updated}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex items-center gap-3">
                        <Link
                          to={`/admin/posts/${p.id}/edit`}
                          data-testid={`admin-edit-${p.id}`}
                          className="text-slate-500 hover:text-[#3366FF]"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </Link>
                        <button
                          data-testid={`admin-delete-${p.id}`}
                          onClick={() => onDelete(p.id, p.title)}
                          className="text-slate-500 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
